import React from 'react';
import { Link } from 'react-router-dom';
import { Flex, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ClientRes, TExpenseRes, TExpenseTypeRes } from 'api/swagger';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import {
  formatPriceInDefault,
  formatPriceInOriginalCurrency,
  isDefaultCurrency,
} from 'shared/utils/currency/format-price.ts';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { ExpenseStatus } from 'types/expense-status';
import { ExpenseStatusTag } from 'components/expenses/expense-status-tag';
import type { IExpensesListProps } from 'components/expenses/expenses-list/types';
import styles from './expenses-list.module.scss';

export const ExpensesList: React.FC<IExpensesListProps> = (props) => {
  const { expenses, isLoading } = props;

  const tableData = useTransformToTableData(expenses);

  const columns: ColumnsType<TExpenseRes> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '10%',
      render: (id: string, { clientNumber }: TExpenseRes) => (
        <Flex align='flex-start' vertical>
          <Link to={`/expenses/${id}`}>{`#${id}`}</Link>
          {clientNumber ? <Typography.Text className={styles.secondary}>{clientNumber}</Typography.Text> : null}
        </Flex>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '10%',
      render: (status) => <ExpenseStatusTag status={status} />,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: '15%',
      render: (type: TExpenseTypeRes) => type?.name,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      width: '12%',
      render: (price, { priceInDefault, currency }: TExpenseRes) => (
        <>
          <div>{formatPriceInDefault(price, priceInDefault, currency)}</div>
          {!isDefaultCurrency(currency) ? (
            <div>
              <Typography.Text type='secondary'>{formatPriceInOriginalCurrency(price, currency)}</Typography.Text>
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: 'End date',
      dataIndex: 'endDate',
      width: '12%',
      render: (endDate) => (endDate ? formatDate(endDate) : null),
    },
    {
      title: 'Partner',
      dataIndex: 'partner',
      width: '12%',
      render: (partner?: ClientRes | null) => {
        if (!partner) return null;

        return <Link to={`/clients/${partner.id}`}>{partner.shortName || partner.name}</Link>;
      },
    },
  ];

  return (
    <div>
      <Table
        rowClassName={(record) => (record.status === ExpenseStatus.Cancelled ? styles.canceledRow : '')}
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
        bordered
      />
    </div>
  );
};
